import Swup from 'swup'
import SwupA11yPlugin from '@swup/a11y-plugin'
import SwupGtmPlugin from '@swup/gtm-plugin'
import SwupPreloadPlugin from '@swup/preload-plugin'
import SwupScrollPlugin from '@swup/scroll-plugin'
import SwupScriptsPlugin from '@swup/scripts-plugin'
import SwupOverlayTheme from '@swup/overlay-theme'
import SwupFragmentPlugin from '@swup/fragment-plugin'
import PageReady from './behaviours/page-ready'
import ImagesLoaded from './behaviours/images-loaded'
import SkipToContent from './behaviours/skip-to-content'
import ToggleDisplay from './behaviours/toggle-display'
import Header from './components/header'
import NewBrandMessage from './components/new-brand-message'
import ToTopButton from './components/to-top-button'
import Tile from './components/tile'
import PeoplePage from './components/people-page'
import ShieldedBadge from './components/shielded-badge'
import ReferrerTracker from './components/referrer-tracker'

// Code highlighting (languages/plugins/css)
import Prism from 'prismjs'
import 'prismjs/components/prism-php'
import 'prismjs/components/prism-bash'
import 'prismjs/components/prism-markup-templating'
import 'prismjs/components/prism-nginx'
import 'prismjs/components/prism-python'
import 'prismjs/components/prism-sql'
import 'prismjs/components/prism-typescript'
import 'prismjs/components/prism-yaml'
import 'prismjs/plugins/line-numbers/prism-line-numbers'
import 'prismjs/plugins/line-numbers/prism-line-numbers.css'
import 'prismjs/themes/prism-okaidia.css'

/**
 * Initialise any components within main element on page transition
 */
const initPageFeatures = () => {
  ImagesLoaded.init()
  Tile.init()
  PeoplePage.init()
  ToggleDisplay.init()
  ReferrerTracker.init()

  // Code highlighting
  if (document.querySelector('.codesampleelement')) {
    Prism.highlightAll()
  }
}

/**
 * Tear down any components within main element on page transition
 */
const killPageFeatures = () => {
  ImagesLoaded.kill()
  Tile.kill()
  PeoplePage.kill()
  ToggleDisplay.kill()
}

/**
 * Initialise any persistent functionality outside of main element
 * (Header, footer etc)
 */
PageReady.ready(() => {
  initPageFeatures()

  // Initialise page transition library
  const swup = new Swup({
    containers: ['#a11y-skip--main'],
    plugins: [
      new SwupA11yPlugin(),
      new SwupGtmPlugin(),
      new SwupPreloadPlugin(),
      new SwupScriptsPlugin({
        head: false,
        body: true,
      }),
      new SwupScrollPlugin({
        animateScroll: window.matchMedia('(prefers-reduced-motion: reduce)').matches
          ? false
          : {
              betweenPages: false,
              samePageWithHash: true,
              samePage: true,
            },
      }),
      new SwupOverlayTheme(),
      new SwupFragmentPlugin({
        rules: [
          {
            from: '/work/tag/:filter?',
            to: '/work/tag/:filter?',
            containers: ['#portfolio-container', '#js-pagination-container'],
          },
          {
            from: '/work',
            to: '/work/tag/:filter?',
            containers: ['#portfolio-container', '#js-pagination-container'],
          },
          {
            from: '/work/tag/:filter?',
            to: '/work',
            containers: ['#portfolio-container', '#js-pagination-container'],
          },
        ],
      }),
    ],
  })

  // Before page transition
  swup.hooks.before('content:replace', () => {
    killPageFeatures()
  })

  // On new page load
  swup.hooks.on('page:view', (visit) => {
    initPageFeatures()

    // Update skip to content link URL (has to include current page URL due to Silverstripe base URL behaviour)
    const skipLink = document.querySelector('.js-a11y-skip-link')
    if (skipLink) {
      skipLink.href = visit.to.url + '#a11y-skip--main'
    }
  })

  // Setup different menu modes (mobile vs desktop)
  Header.init()

  // Show modal if user redirected from old Media Suite domain
  NewBrandMessage.init()

  // Ensure focus is moved to main element on skip to content
  SkipToContent.init()

  // Smooth scroll to top button behaviour
  ToTopButton.init()

  // In footer. See https://shielded.co.nz/
  ShieldedBadge.init()
})
